
import {
    ActionResponse,
    ActionStatusHelper,
    DomainTableConfig,
    useAppEnum,
    useLayoutLifeCycle
} from '@/Shared';

import { PublicationHeader } from '../publication-config';
import { usePublicationEnricher } from '.';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { usePublication } from '@/OneLiners';
import { PublicationViewModel } from '../ViewModels';
import { PublicationModel } from '../Models';

/**
 * usePublicationTableConfig hook
 *
 * Domain-specific layout composition hook for Publication entities.
 * Orchestrates domain data, enrichment, permissions, and UI table configuration.
 *
 * Responsibilities:
 * - Aggregate Publication domain state, enrichment, and permissions into a single response.
 * - Provide dynamic headers for Publication table.
 * - Map strict Model → ViewModel for UI consumption.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 *
 * @see DomainHookProps
 * @see DomainTableConfig<PublicationViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<PublicationViewModel>
 */
export function usePublicationTableConfig(props: DomainHookProps): DomainTableConfig<PublicationViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Publication;

    const { PublicationEnums, PropertyEnums } = useAppEnum();

    const publicationHook = usePublication();

    /**
     * Enrichment layer
     * Responsible for resolving derived Publication data
     */
    const {
        getStrictEnrichedPublication,
        enricherResponse,
    } = usePublicationEnricher();

    /**
     * Combined readiness response
     * Only builds table once all sources succeeded
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            publicationHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: publicationHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Delegation to generic layout lifecycle.
     */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: publicationHook,

        /**
         * Header configuration for Property table.
         */
        headers: PublicationHeader.build(PublicationEnums),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (
            publication: PublicationModel, isDisplaying: boolean) =>
            new PublicationViewModel({
                publication: getStrictEnrichedPublication(publication),
                viewEnums: PublicationEnums,
                accessRight,
                isDisplaying,
                propertyEnums: PropertyEnums
            })
    });

    return lifeCycleHook;
}